import { registerIndicator } from 'klinecharts'

import { type IndicatorSpec, registerIndicatorSpecs, registerOnce } from './registrationUtils'

/**
 * TA-Lib parity indicators — momentum, volatility and volume functions from
 * the TA-Lib catalog that klinecharts does not ship built-in:
 *
 *   Momentum:   PPO, APO, BOP, CMO, IMI, ULTOSC, STOCHF, AROON, AROONOSC,
 *               MFI, STOCHRSI
 *   Volatility: ATR, NATR
 *   Volume:     AD, ADOSC
 *   Statistics: LINREG, LRI, TSF (price pane), LRS, LRA (sub pane)
 *
 * Each indicator's math follows the TA-Lib definition (SMA-seeded EMAs,
 * Wilder smoothing where TA-Lib uses it). Zero-range denominators resolve to
 * the oscillator's neutral value so flat markets render mid-scale instead of
 * gapping.
 */

import {
  type Num,
  type OhlcvBar,
  rollingMean,
  rollingSum,
  emaSeries,
  wilderSeries,
  rsiSeries,
  linregSeries,
  trueRangeSeries,
} from './seriesMath'

export type { OhlcvBar } from './seriesMath'

/* ------------------------------------------------------------------ */
/*  Momentum                                                            */
/* ------------------------------------------------------------------ */

/** PPO — Percentage Price Oscillator with signal line + histogram. */
export function computePPO(
  bars: readonly OhlcvBar[],
  fast: number,
  slow: number,
  signalPeriod: number,
): Array<{ ppo?: number; signal?: number; hist?: number }> {
  const closes = bars.map((b) => b.close)
  const fastEma = emaSeries(closes, fast)
  const slowEma = emaSeries(closes, slow)
  const ppo = bars.map((_, i) => {
    const f = fastEma[i]
    const s = slowEma[i]
    if (f == null || s == null || s === 0) return undefined
    return (100 * (f - s)) / s
  })
  const signal = emaSeries(ppo, signalPeriod)
  return bars.map((_, i) => ({
    ppo: ppo[i],
    signal: signal[i],
    hist: ppo[i] != null && signal[i] != null ? ppo[i]! - signal[i]! : undefined,
  }))
}

/** APO — Absolute Price Oscillator (fast EMA − slow EMA, price units). */
export function computeAPO(
  bars: readonly OhlcvBar[],
  fast: number,
  slow: number,
): Array<{ apo?: number }> {
  const closes = bars.map((b) => b.close)
  const fastEma = emaSeries(closes, fast)
  const slowEma = emaSeries(closes, slow)
  return bars.map((_, i) => {
    const f = fastEma[i]
    const s = slowEma[i]
    return { apo: f != null && s != null ? f - s : undefined }
  })
}

/** BOP — Balance of Power: (close − open) / (high − low), 0 on zero-range bars. */
export function computeBOP(bars: readonly OhlcvBar[]): Array<{ bop: number }> {
  return bars.map((b) => {
    const range = b.high - b.low
    return { bop: range > 0 ? (b.close - b.open) / range : 0 }
  })
}

/** CMO — Chande Momentum Oscillator: 100·(ΣUp − ΣDown)/(ΣUp + ΣDown). */
export function computeCMO(bars: readonly OhlcvBar[], period: number): Array<{ cmo?: number }> {
  const ups: Num[] = new Array(bars.length).fill(undefined)
  const downs: Num[] = new Array(bars.length).fill(undefined)
  for (let i = 1; i < bars.length; i++) {
    const diff = bars[i].close - bars[i - 1].close
    ups[i] = diff > 0 ? diff : 0
    downs[i] = diff < 0 ? -diff : 0
  }
  const upSum = rollingSum(ups, period)
  const downSum = rollingSum(downs, period)
  return bars.map((_, i) => {
    const u = upSum[i]
    const d = downSum[i]
    if (u == null || d == null) return {}
    const denom = u + d
    return { cmo: denom > 0 ? (100 * (u - d)) / denom : 0 }
  })
}

/** IMI — Intraday Momentum Index: RSI-style ratio of bullish vs bearish candle bodies. */
export function computeIMI(bars: readonly OhlcvBar[], period: number): Array<{ imi?: number }> {
  const gains = bars.map((b) => (b.close > b.open ? b.close - b.open : 0))
  const losses = bars.map((b) => (b.open > b.close ? b.open - b.close : 0))
  const gainSum = rollingSum(gains, period)
  const lossSum = rollingSum(losses, period)
  return bars.map((_, i) => {
    const g = gainSum[i]
    const l = lossSum[i]
    if (g == null || l == null) return {}
    const denom = g + l
    return { imi: denom > 0 ? (100 * g) / denom : 50 }
  })
}

/** ULTOSC — Ultimate Oscillator over three lookbacks (weights 4/2/1). */
export function computeULTOSC(
  bars: readonly OhlcvBar[],
  p1: number,
  p2: number,
  p3: number,
): Array<{ ultosc?: number }> {
  const bp: Num[] = new Array(bars.length).fill(undefined)
  const tr: Num[] = new Array(bars.length).fill(undefined)
  for (let i = 1; i < bars.length; i++) {
    const prevClose = bars[i - 1].close
    const lo = Math.min(bars[i].low, prevClose)
    const hi = Math.max(bars[i].high, prevClose)
    bp[i] = bars[i].close - lo
    tr[i] = hi - lo
  }
  const ratio = (bpSum: Num, trSum: Num): number | undefined => {
    if (bpSum == null || trSum == null) return undefined
    return trSum > 0 ? bpSum / trSum : 0.5
  }
  const bp1 = rollingSum(bp, p1)
  const tr1 = rollingSum(tr, p1)
  const bp2 = rollingSum(bp, p2)
  const tr2 = rollingSum(tr, p2)
  const bp3 = rollingSum(bp, p3)
  const tr3 = rollingSum(tr, p3)
  return bars.map((_, i) => {
    const a1 = ratio(bp1[i], tr1[i])
    const a2 = ratio(bp2[i], tr2[i])
    const a3 = ratio(bp3[i], tr3[i])
    if (a1 == null || a2 == null || a3 == null) return {}
    return { ultosc: (100 * (4 * a1 + 2 * a2 + a3)) / 7 }
  })
}

/** STOCHF — Fast Stochastic: raw %K over `kPeriod` bars, %D = SMA(%K, dPeriod). */
export function computeSTOCHF(
  bars: readonly OhlcvBar[],
  kPeriod: number,
  dPeriod: number,
): Array<{ k?: number; d?: number }> {
  const rawK: Num[] = new Array(bars.length).fill(undefined)
  for (let i = kPeriod - 1; i < bars.length; i++) {
    let hh = -Infinity
    let ll = Infinity
    for (let j = i - kPeriod + 1; j <= i; j++) {
      if (bars[j].high > hh) hh = bars[j].high
      if (bars[j].low < ll) ll = bars[j].low
    }
    rawK[i] = hh > ll ? (100 * (bars[i].close - ll)) / (hh - ll) : 50
  }
  const d = rollingMean(rawK, dPeriod)
  return bars.map((_, i) => ({ k: rawK[i], d: d[i] }))
}

/** AROON — bars since the highest high / lowest low inside a `period`+1 window. */
export function computeAROON(
  bars: readonly OhlcvBar[],
  period: number,
): Array<{ up?: number; down?: number }> {
  return bars.map((_, i) => {
    if (i < period) return {}
    let hiIdx = i - period
    let loIdx = i - period
    for (let j = i - period; j <= i; j++) {
      if (bars[j].high >= bars[hiIdx].high) hiIdx = j
      if (bars[j].low <= bars[loIdx].low) loIdx = j
    }
    return {
      up: (100 * (period - (i - hiIdx))) / period,
      down: (100 * (period - (i - loIdx))) / period,
    }
  })
}

/** AROONOSC — Aroon Up − Aroon Down. */
export function computeAROONOSC(
  bars: readonly OhlcvBar[],
  period: number,
): Array<{ osc?: number }> {
  return computeAROON(bars, period).map(({ up, down }) => ({
    osc: up != null && down != null ? up - down : undefined,
  }))
}

/** MFI — Money Flow Index over typical-price money flow. */
export function computeMFI(bars: readonly OhlcvBar[], period: number): Array<{ mfi?: number }> {
  const pos: Num[] = new Array(bars.length).fill(undefined)
  const neg: Num[] = new Array(bars.length).fill(undefined)
  let prevTp: number | undefined
  for (let i = 0; i < bars.length; i++) {
    const tp = (bars[i].high + bars[i].low + bars[i].close) / 3
    if (prevTp !== undefined) {
      const flow = tp * (bars[i].volume ?? 0)
      pos[i] = tp > prevTp ? flow : 0
      neg[i] = tp < prevTp ? flow : 0
    }
    prevTp = tp
  }
  const posSum = rollingSum(pos, period)
  const negSum = rollingSum(neg, period)
  return bars.map((_, i) => {
    const p = posSum[i]
    const n = negSum[i]
    if (p == null || n == null) return {}
    const denom = p + n
    return { mfi: denom > 0 ? (100 * p) / denom : 50 }
  })
}

/** STOCHRSI — stochastic of Wilder RSI, smoothed into %K/%D lines. */
export function computeSTOCHRSI(
  bars: readonly OhlcvBar[],
  rsiPeriod: number,
  stochPeriod: number,
  kPeriod: number,
  dPeriod: number,
): Array<{ k?: number; d?: number }> {
  const rsi = rsiSeries(
    bars.map((b) => b.close),
    rsiPeriod,
  )
  const raw: Num[] = new Array(bars.length).fill(undefined)
  for (let i = 0; i < bars.length; i++) {
    if (i < stochPeriod - 1) continue
    let min = Infinity
    let max = -Infinity
    let full = true
    for (let j = i - stochPeriod + 1; j <= i; j++) {
      const v = rsi[j]
      if (v == null) {
        full = false
        break
      }
      if (v < min) min = v
      if (v > max) max = v
    }
    if (!full) continue
    raw[i] = max > min ? (100 * ((rsi[i] as number) - min)) / (max - min) : 50
  }
  const k = rollingMean(raw, kPeriod)
  const d = rollingMean(k, dPeriod)
  return bars.map((_, i) => ({ k: k[i], d: d[i] }))
}

/* ------------------------------------------------------------------ */
/*  Statistics — rolling linear regression family                       */
/* ------------------------------------------------------------------ */

/** LINREG — least-squares line evaluated at the current bar. */
export function computeLINREG(
  bars: readonly OhlcvBar[],
  period: number,
): Array<{ linreg?: number }> {
  const fits = linregSeries(
    bars.map((b) => b.close),
    period,
  )
  return bars.map((_, i) => {
    const f = fits[i]
    return { linreg: f ? f.intercept + f.slope * (period - 1) : undefined }
  })
}

/** LRI — least-squares line evaluated at the window's oldest bar. */
export function computeLRI(
  bars: readonly OhlcvBar[],
  period: number,
): Array<{ intercept?: number }> {
  const fits = linregSeries(
    bars.map((b) => b.close),
    period,
  )
  return bars.map((_, i) => ({ intercept: fits[i]?.intercept }))
}

/** TSF — Time Series Forecast: the fit projected one bar ahead. */
export function computeTSF(bars: readonly OhlcvBar[], period: number): Array<{ tsf?: number }> {
  const fits = linregSeries(
    bars.map((b) => b.close),
    period,
  )
  return bars.map((_, i) => {
    const f = fits[i]
    return { tsf: f ? f.intercept + f.slope * period : undefined }
  })
}

/** LRS — slope of the least-squares fit (price units per bar). */
export function computeLRS(bars: readonly OhlcvBar[], period: number): Array<{ slope?: number }> {
  const fits = linregSeries(
    bars.map((b) => b.close),
    period,
  )
  return bars.map((_, i) => ({ slope: fits[i]?.slope }))
}

/** LRA — slope expressed as an angle in degrees. */
export function computeLRA(bars: readonly OhlcvBar[], period: number): Array<{ angle?: number }> {
  return computeLRS(bars, period).map(({ slope }) => ({
    angle: slope != null ? (Math.atan(slope) * 180) / Math.PI : undefined,
  }))
}

/* ------------------------------------------------------------------ */
/*  Volatility                                                          */
/* ------------------------------------------------------------------ */

/** ATR — Wilder-smoothed average true range. */
export function computeATR(bars: readonly OhlcvBar[], period: number): Array<{ atr?: number }> {
  const atr = wilderSeries(trueRangeSeries(bars), period)
  return bars.map((_, i) => ({ atr: atr[i] }))
}

/** NATR — ATR normalized to close (percent), comparable across tickers. */
export function computeNATR(bars: readonly OhlcvBar[], period: number): Array<{ natr?: number }> {
  return computeATR(bars, period).map(({ atr }, i) => ({
    natr: atr != null && bars[i].close !== 0 ? (100 * atr) / bars[i].close : undefined,
  }))
}

/* ------------------------------------------------------------------ */
/*  Volume                                                              */
/* ------------------------------------------------------------------ */

/** AD — Chaikin Accumulation/Distribution line (cumulative CLV × volume). */
export function computeAD(bars: readonly OhlcvBar[]): Array<{ ad: number }> {
  let ad = 0
  return bars.map((b) => {
    const range = b.high - b.low
    if (range > 0) {
      const clv = (b.close - b.low - (b.high - b.close)) / range
      ad += clv * (b.volume ?? 0)
    }
    return { ad }
  })
}

/** ADOSC — Chaikin Oscillator: EMA(fast) − EMA(slow) of the A/D line. */
export function computeADOSC(
  bars: readonly OhlcvBar[],
  fast: number,
  slow: number,
): Array<{ adosc?: number }> {
  const ad = computeAD(bars).map((v) => v.ad)
  const fastEma = emaSeries(ad, fast)
  const slowEma = emaSeries(ad, slow)
  return bars.map((_, i) => {
    const f = fastEma[i]
    const s = slowEma[i]
    return { adosc: f != null && s != null ? f - s : undefined }
  })
}

/* ------------------------------------------------------------------ */
/*  Registration                                                        */
/* ------------------------------------------------------------------ */

const SPECS: IndicatorSpec[] = [
  {
    name: 'PPO',
    shortName: 'PPO',
    calcParams: [12, 26, 9],
    precision: 2,
    figures: [
      { key: 'ppo', title: 'PPO: ', type: 'line' },
      { key: 'signal', title: 'SIGNAL: ', type: 'line' },
      { key: 'hist', title: 'HIST: ', type: 'bar' },
    ],
    calc: (data, [fast, slow, signal]) => computePPO(data, fast, slow, signal),
  },
  {
    name: 'APO',
    shortName: 'APO',
    calcParams: [12, 26],
    precision: 2,
    figures: [{ key: 'apo', title: 'APO: ', type: 'line' }],
    calc: (data, [fast, slow]) => computeAPO(data, fast, slow),
  },
  {
    name: 'BOP',
    shortName: 'BOP',
    calcParams: [],
    precision: 4,
    figures: [{ key: 'bop', title: 'BOP: ', type: 'line' }],
    calc: (data) => computeBOP(data),
  },
  {
    name: 'CMO',
    shortName: 'CMO',
    calcParams: [14],
    precision: 2,
    figures: [{ key: 'cmo', title: 'CMO: ', type: 'line' }],
    calc: (data, [period]) => computeCMO(data, period),
  },
  {
    name: 'IMI',
    shortName: 'IMI',
    calcParams: [14],
    precision: 2,
    figures: [{ key: 'imi', title: 'IMI: ', type: 'line' }],
    calc: (data, [period]) => computeIMI(data, period),
  },
  {
    name: 'ULTOSC',
    shortName: 'ULTOSC',
    calcParams: [7, 14, 28],
    precision: 2,
    figures: [{ key: 'ultosc', title: 'ULTOSC: ', type: 'line' }],
    calc: (data, [p1, p2, p3]) => computeULTOSC(data, p1, p2, p3),
  },
  {
    name: 'STOCHF',
    shortName: 'STOCHF',
    calcParams: [14, 3],
    precision: 2,
    figures: [
      { key: 'k', title: 'K: ', type: 'line' },
      { key: 'd', title: 'D: ', type: 'line' },
    ],
    calc: (data, [k, d]) => computeSTOCHF(data, k, d),
  },
  {
    name: 'AROON',
    shortName: 'AROON',
    calcParams: [14],
    precision: 2,
    figures: [
      { key: 'up', title: 'UP: ', type: 'line' },
      { key: 'down', title: 'DOWN: ', type: 'line' },
    ],
    calc: (data, [period]) => computeAROON(data, period),
  },
  {
    name: 'AROONOSC',
    shortName: 'AROONOSC',
    calcParams: [14],
    precision: 2,
    figures: [{ key: 'osc', title: 'OSC: ', type: 'line' }],
    calc: (data, [period]) => computeAROONOSC(data, period),
  },
  {
    name: 'MFI',
    shortName: 'MFI',
    calcParams: [14],
    precision: 2,
    figures: [{ key: 'mfi', title: 'MFI: ', type: 'line' }],
    calc: (data, [period]) => computeMFI(data, period),
  },
  {
    name: 'STOCHRSI',
    shortName: 'STOCHRSI',
    calcParams: [14, 14, 3, 3],
    precision: 2,
    figures: [
      { key: 'k', title: 'K: ', type: 'line' },
      { key: 'd', title: 'D: ', type: 'line' },
    ],
    calc: (data, [rsi, stoch, k, d]) => computeSTOCHRSI(data, rsi, stoch, k, d),
  },
  {
    name: 'ATR',
    shortName: 'ATR',
    calcParams: [14],
    precision: 2,
    figures: [{ key: 'atr', title: 'ATR: ', type: 'line' }],
    calc: (data, [period]) => computeATR(data, period),
  },
  {
    name: 'NATR',
    shortName: 'NATR',
    calcParams: [14],
    precision: 2,
    figures: [{ key: 'natr', title: 'NATR: ', type: 'line' }],
    calc: (data, [period]) => computeNATR(data, period),
  },
  {
    name: 'AD',
    shortName: 'AD',
    calcParams: [],
    precision: 0,
    shouldFormatBigNumber: true,
    figures: [{ key: 'ad', title: 'AD: ', type: 'line' }],
    calc: (data) => computeAD(data),
  },
  {
    name: 'ADOSC',
    shortName: 'ADOSC',
    calcParams: [3, 10],
    precision: 0,
    shouldFormatBigNumber: true,
    figures: [{ key: 'adosc', title: 'ADOSC: ', type: 'line' }],
    calc: (data, [fast, slow]) => computeADOSC(data, fast, slow),
  },
  {
    name: 'LINREG',
    shortName: 'LINREG',
    calcParams: [14],
    precision: 2,
    series: 'price',
    figures: [{ key: 'linreg', title: 'LINREG: ', type: 'line' }],
    calc: (data, [period]) => computeLINREG(data, period),
  },
  {
    name: 'LRI',
    shortName: 'LRI',
    calcParams: [14],
    precision: 2,
    series: 'price',
    figures: [{ key: 'intercept', title: 'LRI: ', type: 'line' }],
    calc: (data, [period]) => computeLRI(data, period),
  },
  {
    name: 'TSF',
    shortName: 'TSF',
    calcParams: [14],
    precision: 2,
    series: 'price',
    figures: [{ key: 'tsf', title: 'TSF: ', type: 'line' }],
    calc: (data, [period]) => computeTSF(data, period),
  },
  {
    name: 'LRS',
    shortName: 'LRS',
    calcParams: [14],
    precision: 4,
    figures: [{ key: 'slope', title: 'SLOPE: ', type: 'line' }],
    calc: (data, [period]) => computeLRS(data, period),
  },
  {
    name: 'LRA',
    shortName: 'LRA',
    calcParams: [14],
    precision: 2,
    figures: [{ key: 'angle', title: 'ANGLE: ', type: 'line' }],
    calc: (data, [period]) => computeLRA(data, period),
  },
]

/** Names of every indicator this module registers (used by pickers/tests). */
export const TALIB_INDICATOR_NAMES = SPECS.map((s) => s.name)

/** Register all TA-Lib parity indicators with klinecharts (idempotent). */
export const registerTaLibIndicators = registerOnce(() => {
  registerIndicatorSpecs(registerIndicator, SPECS)
})
